import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { MqttClient } from 'mqtt';

// --- Wire types (match Go mqttchat.FsRequest / FsResponse) ---

interface FsRequest {
  id: string;
  op: 'ls' | 'tree' | 'stat' | 'read';
  path: string;
  depth?: number;
  offset?: number;
  limit?: number;
  ts: number;
}

interface FsResponse {
  id: string;
  op?: string;
  path?: string;
  error?: string;
  entries?: DirEntry[];
  tree?: TreeNode[];
  content?: string;
  offset?: number;
  limit?: number;
  total_lines?: number;
  size?: number;
  is_dir?: boolean;
  is_binary?: boolean;
  lang?: string;
  truncated?: boolean;
  modified?: string;
}

interface DirEntry {
  name: string;
  is_dir: boolean;
  size: number;
  modified: string;
}

interface TreeNode {
  path: string;
  is_dir: boolean;
  size?: number;
  children?: TreeNode[];
}

// --- File viewer state ---

interface FileViewerState {
  path: string;
  content: string;
  offset: number;
  limit: number;
  totalLines: number;
  fileSize: number;
  lang: string;
  loading: boolean;
}

// --- Props ---

interface FileBrowserPanelProps {
  client: MqttClient;
  agentId: string;
  open: boolean;
  onClose: () => void;
  /** When true, renders inline (full-width, no fixed overlay) instead of slide-over */
  inline?: boolean;
}

const LINES_PER_PAGE = 200;

function humanSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${bytes}B`;
}

function reqId(): string {
  return `fs-${crypto.randomUUID().slice(0, 8)}`;
}

// ============================================================================
// FileBrowserPanel
// ============================================================================

const FileBrowserPanel: React.FC<FileBrowserPanelProps> = ({ client, agentId, open, onClose, inline }) => {
  const [view, setView] = useState<'tree' | 'file'>('tree');
  const [entries, setEntries] = useState<Map<string, DirEntry[]>>(new Map());
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set());
  const [file, setFile] = useState<FileViewerState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [goToLine, setGoToLine] = useState('');

  const pendingIds = useRef(new Map<string, (resp: FsResponse) => void>());
  const subscribedRef = useRef(false);
  const resTopic = `flux/${agentId}/fs/res`;
  const reqTopic = `flux/${agentId}/fs/req`;

  // Subscribe / unsubscribe to fs/res
  useEffect(() => {
    if (!open) return;

    const handler = (_topic: string, payload: Buffer) => {
      try {
        const resp: FsResponse = JSON.parse(payload.toString());
        const cb = pendingIds.current.get(resp.id);
        if (cb) {
          pendingIds.current.delete(resp.id);
          cb(resp);
        }
      } catch { /* ignore malformed */ }
    };

    client.subscribe(resTopic, { qos: 0 });
    client.on('message', handler);
    subscribedRef.current = true;

    return () => {
      client.unsubscribe(resTopic);
      client.removeListener('message', handler);
      subscribedRef.current = false;
      pendingIds.current.clear();
    };
  }, [client, agentId, open, resTopic]);

  // Load root on open
  useEffect(() => {
    if (open && entries.size === 0) {
      loadDir('.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sendRequest = useCallback((req: Omit<FsRequest, 'id' | 'ts'>): Promise<FsResponse> => {
    return new Promise((resolve, reject) => {
      const id = reqId();
      const full: FsRequest = { ...req, id, ts: Date.now() } as FsRequest;

      const timeout = setTimeout(() => {
        pendingIds.current.delete(id);
        reject(new Error('Request timed out'));
      }, 10000);

      pendingIds.current.set(id, (resp) => {
        clearTimeout(timeout);
        resolve(resp);
      });

      client.publish(reqTopic, JSON.stringify(full), { qos: 1 });
    });
  }, [client, reqTopic]);

  const loadDir = useCallback(async (path: string) => {
    setError(null);
    setLoading(true);
    try {
      const resp = await sendRequest({ op: 'ls', path });
      if (resp.error) {
        setError(resp.error);
      } else if (resp.entries) {
        setEntries(prev => new Map(prev).set(path, resp.entries!));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [sendRequest]);

  const toggleDir = useCallback(async (path: string) => {
    const next = new Set(expandedDirs);
    if (next.has(path)) {
      next.delete(path);
    } else {
      next.add(path);
      if (!entries.has(path)) {
        await loadDir(path);
      }
    }
    setExpandedDirs(next);
  }, [expandedDirs, entries, loadDir]);

  const openFile = useCallback(async (path: string) => {
    setError(null);
    setLoading(true);
    setView('file');
    try {
      const resp = await sendRequest({ op: 'read', path, offset: 0, limit: LINES_PER_PAGE });
      if (resp.error) {
        setError(resp.error);
        setView('tree');
      } else {
        setFile({
          path,
          content: resp.content || '',
          offset: resp.offset || 0,
          limit: resp.limit || LINES_PER_PAGE,
          totalLines: resp.total_lines || 0,
          fileSize: resp.size || 0,
          lang: resp.lang || '',
          loading: false,
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
      setView('tree');
    } finally {
      setLoading(false);
    }
  }, [sendRequest]);

  const navigatePage = useCallback(async (newOffset: number) => {
    if (!file) return;
    setFile(prev => prev ? { ...prev, loading: true } : null);
    try {
      const resp = await sendRequest({ op: 'read', path: file.path, offset: newOffset, limit: LINES_PER_PAGE });
      if (resp.error) {
        setError(resp.error);
      } else {
        setFile(prev => prev ? {
          ...prev,
          content: resp.content || '',
          offset: resp.offset || 0,
          totalLines: resp.total_lines || prev.totalLines,
          loading: false,
        } : null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
      setFile(prev => prev ? { ...prev, loading: false } : null);
    }
  }, [file, sendRequest]);

  const handleGoToLine = useCallback(() => {
    const line = parseInt(goToLine, 10);
    if (isNaN(line) || line < 1 || !file) return;
    const offset = Math.floor((line - 1) / LINES_PER_PAGE) * LINES_PER_PAGE;
    navigatePage(offset);
    setGoToLine('');
  }, [goToLine, file, navigatePage]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (view === 'file') {
          setView('tree');
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, view, onClose]);

  const fileName = file?.path.split('/').pop() || '';
  const totalPages = file ? Math.ceil(file.totalLines / LINES_PER_PAGE) : 0;
  const currentPage = file ? Math.floor(file.offset / LINES_PER_PAGE) + 1 : 0;

  const panelHeader = (
    <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700 flex-shrink-0">
      {view === 'file' ? (
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => setView('tree')}
            className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-gray-200 flex-shrink-0"
            title="Back to tree"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-medium text-gray-200 truncate">{fileName}</span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <span className="text-sm font-medium text-gray-200">Files</span>
        </div>
      )}
      {!inline && (
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-gray-200"
          title="Close (Esc)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );

  const panelContent = (
    <>
      {/* Error banner */}
      {error && (
        <div className="px-3 py-2 bg-red-900/50 border-b border-red-700 text-red-300 text-xs flex items-center justify-between flex-shrink-0">
          <span className="truncate">{error}</span>
          <button onClick={() => setError(null)} className="ml-2 text-red-400 hover:text-red-200 flex-shrink-0">&times;</button>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="h-0.5 bg-blue-500 animate-pulse flex-shrink-0" />
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {view === 'tree' ? (
          <FileTree
            entries={entries}
            expandedDirs={expandedDirs}
            onToggleDir={toggleDir}
            onOpenFile={openFile}
          />
        ) : file ? (
          <FileViewer
            file={file}
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={() => navigatePage(file.offset - LINES_PER_PAGE)}
            onNext={() => navigatePage(file.offset + LINES_PER_PAGE)}
            goToLine={goToLine}
            onGoToLineChange={setGoToLine}
            onGoToLine={handleGoToLine}
          />
        ) : null}
      </div>
    </>
  );

  // Inline mode: render directly without fixed positioning
  if (inline) {
    return (
      <div className="flex flex-col h-full bg-gray-900">
        {panelHeader}
        {panelContent}
      </div>
    );
  }

  return (
    <>
      {/* Backdrop on mobile */}
      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full z-40 bg-gray-900 border-l border-gray-700 flex flex-col
          w-full md:w-[400px] transition-transform duration-200 ease-in-out
          ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {panelHeader}
        {panelContent}
      </div>
    </>
  );
};

// ============================================================================
// FileTree
// ============================================================================

interface FileTreeProps {
  entries: Map<string, DirEntry[]>;
  expandedDirs: Set<string>;
  onToggleDir: (path: string) => void;
  onOpenFile: (path: string) => void;
}

const FileTree: React.FC<FileTreeProps> = ({ entries, expandedDirs, onToggleDir, onOpenFile }) => {
  const rootEntries = entries.get('.') || [];

  if (rootEntries.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8 text-sm">
        Loading...
      </div>
    );
  }

  return (
    <div className="py-1">
      {rootEntries.map(entry => (
        <TreeRow
          key={entry.name}
          entry={entry}
          path={entry.name}
          depth={0}
          entries={entries}
          expandedDirs={expandedDirs}
          onToggleDir={onToggleDir}
          onOpenFile={onOpenFile}
        />
      ))}
    </div>
  );
};

// ============================================================================
// TreeRow (recursive)
// ============================================================================

interface TreeRowProps {
  entry: DirEntry;
  path: string;
  depth: number;
  entries: Map<string, DirEntry[]>;
  expandedDirs: Set<string>;
  onToggleDir: (path: string) => void;
  onOpenFile: (path: string) => void;
}

const TreeRow: React.FC<TreeRowProps> = ({ entry, path, depth, entries, expandedDirs, onToggleDir, onOpenFile }) => {
  const isExpanded = expandedDirs.has(path);
  const children = entries.get(path) || [];

  return (
    <>
      <button
        className="w-full flex items-center gap-1.5 px-2 py-1 text-xs hover:bg-gray-800 text-left group"
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => entry.is_dir ? onToggleDir(path) : onOpenFile(path)}
        title={path}
      >
        {/* Expand/collapse chevron or file icon */}
        {entry.is_dir ? (
          <svg className={`w-3 h-3 text-gray-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
               fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        ) : (
          <svg className="w-3 h-3 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )}

        {/* Icon color for folders */}
        {entry.is_dir ? (
          <svg className="w-4 h-4 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
        ) : null}

        <span className={`truncate ${entry.is_dir ? 'text-gray-200' : 'text-gray-400 group-hover:text-gray-200'}`}>
          {entry.name}
        </span>

        {!entry.is_dir && entry.size > 0 && (
          <span className="ml-auto text-gray-600 text-[10px] flex-shrink-0">
            {humanSize(entry.size)}
          </span>
        )}
      </button>

      {/* Expanded children */}
      {entry.is_dir && isExpanded && children.map(child => (
        <TreeRow
          key={child.name}
          entry={child}
          path={`${path}/${child.name}`}
          depth={depth + 1}
          entries={entries}
          expandedDirs={expandedDirs}
          onToggleDir={onToggleDir}
          onOpenFile={onOpenFile}
        />
      ))}
    </>
  );
};

// ============================================================================
// FileViewer
// ============================================================================

interface FileViewerProps {
  file: FileViewerState;
  currentPage: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  goToLine: string;
  onGoToLineChange: (value: string) => void;
  onGoToLine: () => void;
}

const FileViewer: React.FC<FileViewerProps> = ({
  file, currentPage, totalPages, onPrev, onNext,
  goToLine, onGoToLineChange, onGoToLine,
}) => {
  const lines = file.content ? file.content.split('\n') : [];
  const startLine = file.offset + 1;
  const endLine = file.offset + lines.length;

  return (
    <div className="flex flex-col h-full">
      {/* File info bar */}
      <div className="px-3 py-1.5 bg-gray-800/50 border-b border-gray-700 text-[10px] text-gray-500 flex items-center gap-2 flex-shrink-0">
        <span>{file.totalLines} lines</span>
        <span>&middot;</span>
        <span>{humanSize(file.fileSize)}</span>
        {file.lang && (
          <>
            <span>&middot;</span>
            <span className="text-gray-400">{file.lang}</span>
          </>
        )}
      </div>

      {/* Code content */}
      <div className="flex-1 min-h-0 overflow-auto">
        {file.loading ? (
          <div className="text-center text-gray-500 py-8 text-sm">Loading...</div>
        ) : (
          <pre className="text-xs leading-5 font-mono">
            {lines.map((line, i) => (
              <div key={file.offset + i} className="flex hover:bg-gray-800/50 px-1">
                <span className="w-12 text-right text-gray-600 pr-3 select-none flex-shrink-0">
                  {file.offset + i + 1}
                </span>
                <span className="text-gray-300 whitespace-pre overflow-x-auto">{line}</span>
              </div>
            ))}
          </pre>
        )}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="px-3 py-2 bg-gray-800 border-t border-gray-700 flex-shrink-0 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <button
              onClick={onPrev}
              disabled={currentPage <= 1}
              className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-gray-300"
            >
              Prev
            </button>
            <span className="text-gray-500">
              Page {currentPage}/{totalPages} (lines {startLine}&ndash;{endLine})
            </span>
            <button
              onClick={onNext}
              disabled={currentPage >= totalPages}
              className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-gray-300"
            >
              Next
            </button>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-gray-500">Go to line:</label>
            <input
              type="number"
              min={1}
              max={file.totalLines}
              value={goToLine}
              onChange={(e) => onGoToLineChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onGoToLine()}
              className="w-20 px-1.5 py-0.5 bg-gray-700 border border-gray-600 rounded text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="line #"
            />
            <button
              onClick={onGoToLine}
              className="px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-xs text-gray-300"
            >
              Go
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileBrowserPanel;
